import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { CourseCompletionQuizIntro } from "@/components/courseCompletionQuiz/CourseCompletionQuizIntro";
import { ErrorState } from "@/components/common/ErrorState";
import { LoadingState } from "@/components/common/LoadingState";
import { useCourseTitleInHeader } from "@/hooks/courseCompletionQuiz/useCourseTitleInHeader";
import { useEnrollment } from "@/hooks/useEnrollment";
import { courseCompletionQuizPath } from "@/lib/courseCompletionQuiz";
import { startErrorMessage } from "@/lib/courseCompletionQuizMessages";
import { QuizValidationService } from "@/services/QuizValidationService";

/** Introduction of the course completion quiz; starting it opens the run. */
export const CourseCompletionQuizIntroPage = () => {
  const navigate = useNavigate();
  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const { data: enrollment, isLoading, error } = useEnrollment(enrollmentId);
  useCourseTitleInHeader(enrollment?.courseTitle);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  if (!enrollmentId) {
    return (
      <ErrorState
        title="Kursabschlussquiz konnte nicht geladen werden"
        message="Keine Einschreibungs-ID in der URL"
      />
    );
  }
  if (isLoading) return <LoadingState message="Quiz wird geladen..." />;
  if (error || !enrollment) {
    return (
      <ErrorState
        title="Kursabschlussquiz konnte nicht geladen werden"
        message={error?.message ?? "Einschreibung nicht gefunden"}
      />
    );
  }

  const quiz = enrollment.courseCompletionQuiz;
  const dashboard = `/course/${enrollmentId}/dashboard`;
  if (!quiz || quiz.status === "locked") {
    return <Navigate to={dashboard} replace />;
  }
  // This page is only for the start: an open run or a finished one lives elsewhere.
  if (quiz.status !== "startable") {
    return (
      <Navigate
        to={courseCompletionQuizPath(enrollmentId, quiz.status)}
        replace
      />
    );
  }

  const startQuiz = async () => {
    setStarting(true);
    setStartError(null);
    try {
      const result =
        await QuizValidationService.startCourseCompletionQuiz(enrollmentId);
      if (result.ok) {
        navigate(courseCompletionQuizPath(enrollmentId, "in_progress"));
        return;
      }
      setStartError(startErrorMessage(result.error, quiz.unlockPercent));
    } catch {
      setStartError(
        "Das Quiz konnte nicht gestartet werden. Bitte versuche es noch einmal."
      );
    }
    setStarting(false);
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 md:px-8">
      <CourseCompletionQuizIntro
        summary={quiz}
        courseTitle={enrollment.courseTitle}
        starting={starting}
        error={startError}
        onStart={() => void startQuiz()}
        onBack={() => navigate(dashboard)}
      />
    </div>
  );
};
